import getHostname from './getHostname';
import * as cookies from 'js-cookie';
import { format as urlFormat } from 'url';
import prepareSearchParams, { QueryMap } from './prepareSearchParams';
import StatusError, { createStatusError } from './statusError';
import prepareHeaders from './prepareHeaders';
import ExceptionLogger from '~/libs/api-hooks/architect/backplane/makeExceptionLogger';

export type HttpMethods = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface UrlOptions {
  subdomain?: string;
  hostname?: string;
  port?: string;
  protocal?: 'https';
}

export interface BodyType {
  [key: string]:
    | number
    | number[]
    | boolean
    | boolean[]
    | string
    | string[]
    | null
    | BodyType
    | BodyType[];
}

export interface CCRequest {
  pathname: string;
  queryMap?: QueryMap;
  body?: BodyType | BodyType[];
  method: HttpMethods;
  urlOptions?: UrlOptions;
  includeApiKeyWhenAvailable: boolean;
  customHeaders?: Record<string, string>;
  sendCsrfToken: boolean;
  timeoutMs?: number;
}

const credentials = (apiKey?: string) => (apiKey ? 'same-origin' : 'include');

const fetchWithTimeout = async (
  url: string,
  options: RequestInit,
  timeoutMs?: number,
): Promise<Response> => {
  if (!timeoutMs) {
    return fetch(url, options);
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    return response;
  } catch (error: any) {
    if (error?.name === 'AbortError') {
      throw new Error('Request timeout');
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
};

const requester = async ({
  pathname,
  queryMap = {},
  body,
  method,
  urlOptions,
  includeApiKeyWhenAvailable,
  customHeaders,
  sendCsrfToken,
  timeoutMs,
}: CCRequest): Promise<Response> => {
  try {
    const apiKey = includeApiKeyWhenAvailable
      ? localStorage.getItem('CIRCLE_TOKEN') || cookies.get('circle-token')
      : '';
    const url = urlFormat({
      protocol: 'https',
      ...urlOptions,
      hostname: getHostname(urlOptions?.hostname, urlOptions?.subdomain),
      pathname,
      search: prepareSearchParams(queryMap),
    });

    const headers = await prepareHeaders({
      apiKey,
      method,
      sendCsrfToken,
      customHeaders,
    });

    const response = await fetchWithTimeout(
      url,
      {
        method,
        headers,
        body: JSON.stringify(body),
        credentials: credentials(apiKey),
      },
      timeoutMs,
    );
    if (!response.ok) {
      throw await createStatusError(pathname, response);
    }
    return response;
    // TODO: this should be typed
  } catch (error: any) {
    let statusError: StatusError;
    if (error.status) {
      statusError = error;
    } else if (error.message === 'hostname is not configured') {
      statusError = new StatusError({
        message: error.message,
        status: 0,
        pathname,
      });
    } else {
      statusError = new StatusError({
        message: `[Network Error] ${error.message}`,
        status: 503,
        pathname,
      });
    }
    if (error.status >= 500 || error.status === 0) {
      ExceptionLogger().error(error.message, { error });
    }
    throw statusError;
  }
};

export default requester;
